.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.label {
  font-size: var(--size-200);
  font-weight: var(--weight-medium);

  color: var(--text);
}

.optional {
  margin-inline-start: var(--space-2xs);

  font-weight: var(--weight-regular);

  color: var(--text-faint);
}

.hint {
  font-size: var(--size-100);

  color: var(--text-muted);
}

.input,
.textarea {
  width: 100%;

  padding: var(--space-xs) var(--space-s);

  font-size: var(--size-300);

  color: var(--text);
  background-color: var(--field);

  border: 1px solid var(--border-strong);
  border-radius: var(--radius-s);

  transition: border-color var(--duration-fast) var(--ease), box-shadow var(--duration-fast) var(--ease);
}

.input:focus,
.textarea:focus {
  border-color: var(--accent);
  box-shadow: var(--ring);

  outline: none;
}

.textarea {
  min-height: 12rem;

  line-height: var(--leading-normal);

  resize: vertical;
}

.choices {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
}

.choice {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);

  padding: var(--space-xs) var(--space-s);

  font-size: var(--size-200);

  background-color: var(--field);

  border: 1px solid var(--border-strong);
  border-radius: var(--radius-s);

  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease),
    background-color var(--duration-fast) var(--ease);
}

.choice:hover:not(:has(input:checked, input:disabled)) {
  border-color: var(--accent);
}

.choice:has(input:focus-visible) {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

.choice:has(input:checked) {
  color: var(--accent-strong);
  background-color: var(--accent-soft);
  border-color: var(--accent);
}

.scale {
  display: flex;
  gap: var(--space-2xs);
}

.step {
  position: relative;

  min-width: 2.5rem;

  padding: var(--space-xs) 0;

  text-align: center;
  font-variant-numeric: tabular-nums;

  cursor: pointer;
}

/*
 * The relevancy scale, from irrelevant to most relevant. The colours live in
 * the tokens so the read-only mark on the personas screen paints the same five.
 */
$relevancy-track: var(--field);
$relevancy-much-less: var(--relevancy-much-less);
$relevancy-less: var(--relevancy-less);
$relevancy-equal: var(--relevancy-equal);
$relevancy-more: var(--relevancy-more);
$relevancy-much-more: var(--relevancy-much-more);

.relevancy {
  display: flex;

  padding: 4px;

  background-color: var(--surface-raised);

  border-radius: 9px;

  &:has(input:checked) {
      background-color: $relevancy-track;
  }
}

.relevancyStep {
  position: relative;

  flex: 1;
  min-width: 2.25rem;

  padding: var(--space-2xs) 0;

  text-align: center;
  font-size: var(--size-200);
  font-weight: var(--weight-strong);
  font-variant-numeric: tabular-nums;

  color: #fff;

  border-radius: var(--radius-s);

  cursor: pointer;
  transition: background-color var(--duration-fast) var(--ease),
    transform var(--duration-fast) var(--ease), color var(--duration-fast) var(--ease);

    &:hover {
        background-color: var(--surface-sunken);
    }
}

.relevancyStep:has(input:focus-visible) {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

// .muchLess {
//   background-color: $relevancy-much-less;
// }

// .less {
//   background-color: $relevancy-less;
// }

// .equal {
//   background-color: $relevancy-equal;
// }

// .more {
//   background-color: $relevancy-more;
// }

// .muchMore {
//   background-color: $relevancy-much-more;
// }

/*
 * Once a step is picked, the others give up their colour and the chosen one
 * grows a little, so a page of these reads as answers rather than as a palette.
 */
.relevancyPicked .relevancyStep {
  color: rgb(255 255 255 / 0.45);
  background-color: transparent;
}

.relevancyPicked .relevancyStep:has(input:checked) {
  color: #fff;
}

.relevancyPicked .muchLess:has(input:checked) {
  background-color: $relevancy-much-less;
}

.relevancyPicked .less:has(input:checked) {
  background-color: $relevancy-less;
}

.relevancyPicked .equal:has(input:checked) {
  background-color: $relevancy-equal;
}

.relevancyPicked .more:has(input:checked) {
  background-color: $relevancy-more;
}

.relevancyPicked .muchMore:has(input:checked) {
  background-color: $relevancy-much-more;
}

/*
 * The rating scale paints its own steps, so the radio itself must stay out of
 * sight without leaving the tab order: `hidden` would take it off the keyboard.
 */
.hiddenControl {
  position: absolute;
  width: 1px;
  height: 1px;

  opacity: 0;
}

.large {
  padding: var(--space-xs) var(--space-l);

  font-size: var(--size-300);
}

.demanded {
  margin-inline-start: var(--space-3xs);

  color: var(--band-missing);
}

.problem {
  font-size: var(--size-100);

  color: var(--band-missing);
}

.fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--space-s);

  margin: 0;
  padding: 0;

  border: none;
}

.fieldset:disabled {
  opacity: 0.55;
}

.legend {
  padding: 0;

  font-size: var(--size-200);
  font-weight: var(--weight-medium);
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);
}

.button {
  display: inline-flex;
  align-items: center;

  padding: var(--space-xs) var(--space-m);

  font-size: var(--size-200);
  font-weight: var(--weight-medium);

  border: 1px solid var(--border-strong);
  border-radius: var(--radius-m);

  cursor: pointer;
  transition: background-color var(--duration-fast) var(--ease), border-color var(--duration-fast) var(--ease);
}

.button:disabled {
    opacity: 0.5;

  cursor: not-allowed;
}

.quiet {
  color: var(--text);
  background-color: transparent;
}

.quiet:hover:not(:disabled) {
  color: var(--accent-strong);
  border-color: var(--accent);
}

/* Dark type on the petrol fill: the accent is bright enough to carry it. */
.primary {
  color: var(--on-accent);
  background-color: var(--accent);
  border-color: var(--accent);

  font-weight: var(--weight-strong);
}

.primary:hover:not(:disabled) {
  background-color: var(--accent-strong);
  border-color: var(--accent-strong);
}

.danger {
  color: var(--band-missing);
  background-color: var(--band-tint-missing);
  border-color: var(--band-missing);
}

/*
 * A glyph-only button. It is sized from the icon's own em box so it stays square
 * whatever the surrounding font size is.
 */
.iconOnly {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: var(--space-2xs);

  font-size: var(--size-300);

  color: var(--text-muted);
  background-color: transparent;
  border-color: transparent;
}

.iconOnly:hover:not(:disabled) {
  color: var(--text);
  background-color: var(--surface-raised);
  border-color: var(--border-strong);
}

/*
 * A flat many-of-many list. It scrolls at the same height as a tree so a long
 * list of questionnaires cannot push the form's buttons out of reach.
 */
.checkList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  max-height: 460px;
  padding: var(--space-xs) var(--space-s);

  background-color: var(--field);

  border: 1px solid var(--border-strong);
  border-radius: var(--radius-s);

  overflow-y: auto;
}

.check {
  display: flex;
  align-items: baseline;
  gap: var(--space-xs);

  font-size: var(--size-300);

  cursor: pointer;
}

/* A glyph ahead of a button's label, kept on the text baseline rather than the box. */
.buttonIcon {
  display: inline-flex;
  align-items: center;

  margin-inline-end: var(--space-2xs);

  vertical-align: -0.15em;
}
